import {
  ActivityLessonVideo,
  ActivityType,
  CollectionStats,
} from '@fenderdigital/api-definitions/play';
import { Instrument } from '../utils/path-utils';

// Returned from the backend Search API
// Ideally these would live and be managed by the backend as they're subject to change via backend
export interface SearchItem {
  objectID: string;
  id?: string; // this seems to only be on Artist from the backend
  objectType: AlgoliaObjectType;
  title: string;
  level?: number;
  image: string;
  type: AlgoliaType;
  instrument: string;
  url?: string;
  category?: string;
  artists?: string[];
  stats?: CollectionStats;
}

interface ArtistNamesCount {
  [key: string]: number;
}

export interface SearchUserData {
  category?: string;
  description?: string;
  image?: string;
  title?: string;
  url?: string;
  trackEvent?: Record<string, unknown>;
}

export interface Result {
  fullyHighlighted?: boolean;
  matchLevel: string;
  matchedWords: string[] | [];
  value: string;
}

export interface Artist {
  name?: Result;
}

export interface HighlightResult {
  artists?: Artist[];
  instrument?: Result;
  level?: Result;
  slug?: Result;
  style?: Result;
  title?: Result;
  type?: Result;
}

export interface Hit {
  'access-levels'?: string[] | null;
  'activity-lesson-video'?: ActivityLessonVideo;
  'activity-type'?: ActivityType;
  artists?: ArtistInSearch[];
  category?: string;
  description?: string;
  duration?: number;
  image?: string;
  instrument?: string | any;
  lessonCount?: number;
  level?: string; // not a number
  objectID?: string;
  objectType?: AlgoliaObjectType | any;
  slug?: string;
  style?: AlgoliaStyle;
  stats?: CollectionStats;
  title?: string;
  type?: AlgoliaType | string | any;
  trackEvent?: Record<string, unknown>;
  url?: string;
  _highlightResult?: HighlightResult | any;
  overlayTitle?: string;
  overlayColor?: string;
  name?: string;
}

export interface Facets {
  'artists.id'?: string;
  'artists.name'?: ArtistNamesCount;
  style?: AlgoliaStyleMap;
  type?: AlgoliaTypeMap;
  objectType?: AlgoliaObjectTypeMap;
  description?: string;
  instrument?: AlgoliaInstrumentMap;
  lessonCount?: number;
  level?: string; // not a number
}

export interface SummaryItem {
  items: any[];
  title: string;
  total: string | number;
  url: string;
}

export interface SummarySections {
  collections: SummaryItem;
  song: SummaryItem;
  riff: SummaryItem;
  artists: SummaryItem;
  skill: SummaryItem;
}

export enum SummarySectionTitle {
  Collection = 'collection',
  Song = 'song',
  Riff = 'riff',
  Artist = 'artist',
  Skill = 'skill',
}

export interface ArtistInSearch {
  id: string;
  name: string;
  image: string;
}

export interface HitOverlay {
  overlayColor: string;
  overlayTitle: string;
}

export interface FilterItem {
  id: string;
  title: string;
  subTitle: string | number;
  url: string;
}

export enum HighlightedFacetType {
  Artists,
  Title,
}

export enum FacetFilterType {
  Instrument = 'instrument',
  Type = 'type',
  ObjectType = 'objectType',
  Style = 'style',
  ArtistName = 'artist.name',
  level = 'level',
}

export interface FilterData {
  instrument?: InstrumentGroup | Instrument;
  style?: AlgoliaStyle;
  type?: AlgoliaType | any;
  objectType?: AlgoliaObjectType;
  'artist.name'?: string;
  level?: string;
}

export enum InstrumentGroup {
  // The main instrument group on the frontend
  Guitar = 'guitar',
  Ukulele = 'ukulele',
  Bass = 'bass',
}

// These are defined in Algolia under "instrument"
export enum AlgoliaInstrument {
  AcousticGuitar = 'acoustic-guitar',
  ElectricGuitar = 'electric-guitar',
  Ukulele = 'ukulele',
  Bass = 'bass',
  Unassigned = 'unassigned',
}

export type AlgoliaInstrumentMap = {
  [key in AlgoliaInstrument]: string;
};

// These are defined in Algolia under "style"
export enum AlgoliaStyle {
  Rock = 'rock',
  Pop = 'pop',
  Country = 'country',
  Blues = 'blues',
  Folk = 'folk',
  Funky = 'funk',
}

export type AlgoliaStyleMap = {
  [key in AlgoliaStyle]: string;
};

// These are defined in Algolia under "type"
export enum AlgoliaType {
  song = 'song',
  exercise = 'exercise',
  chord = 'chord',
  skill = 'skill',
  basic = 'basic',
  riff = 'riff',
  glossary = 'glossary',
  theory = 'theory',
  technique = 'technique',
  tone = 'tone',
}

export type AlgoliaTypeMap = {
  [key in AlgoliaType]: string | number;
};

// These are defined in Algolia under "objectType"
export enum AlgoliaObjectType {
  course = 'course',
  lesson = 'lesson',
  collection = 'collection',
}

// These are defined in Algolia under "eventType"
export type AlgoliaEventType = 'view' | 'click' | 'conversion';
export type AlgoliaFilters = Array<{ type: string; value: string }>;

export type AlgoliaObjectTypeMap = {
  [key in AlgoliaObjectType]: string;
};

export type AlgoliaFacetFilters = Array<string | string[]>;
